import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export interface LightboxItem {
  src: string;
  srcSet?: string;
  sizes?: string;
  alt: string;
  caption?: string | null;
  category?: string;
  width?: number;
  height?: number;
}

interface Props {
  items: LightboxItem[];
  index: number | null;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}

/**
 * Accessible, responsive lightbox.
 * - role="dialog" aria-modal with title + description
 * - ESC to close, arrow keys to navigate, Home/End for first/last
 * - Focus is trapped within the dialog while open
 * - Returns focus to the trigger that opened it on close
 * - Honours prefers-reduced-motion (no entrance scale)
 * - Backdrop click closes; image click does not
 */
export function GalleryLightbox({ items, index, onClose, onIndexChange }: Props) {
  const open = index !== null && index >= 0 && index < items.length;
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  const goPrev = useCallback(() => {
    if (index === null) return;
    onIndexChange((index - 1 + items.length) % items.length);
  }, [index, items.length, onIndexChange]);

  const goNext = useCallback(() => {
    if (index === null) return;
    onIndexChange((index + 1) % items.length);
  }, [index, items.length, onIndexChange]);

  // Capture trigger element to restore focus on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = (typeof document !== "undefined" ? (document.activeElement as HTMLElement | null) : null);
    // Focus the close button so screen-reader users land on a known action.
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);
    // Lock body scroll while open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = prevOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  // Keyboard handling + focus trap.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); goNext(); return; }
      if (e.key === "Home") { e.preventDefault(); onIndexChange(0); return; }
      if (e.key === "End") { e.preventDefault(); onIndexChange(items.length - 1); return; }
      if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose, goPrev, goNext, onIndexChange, items.length]);

  if (!open || typeof document === "undefined") return null;
  const item = items[index!];
  const positionLabel = `Photo ${index! + 1} of ${items.length}`;
  const caption = item.caption || item.alt;

  return createPortal(
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="lightbox-title"
      aria-describedby="lightbox-caption"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm motion-safe:animate-in motion-safe:fade-in"
      onClick={onClose}
    >
      <h2 id="lightbox-title" className="sr-only">{positionLabel}: {item.alt}</h2>

      {/* Close */}
      <button
        ref={closeBtnRef}
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close gallery"
        className="absolute top-3 right-3 md:top-5 md:right-5 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
      >
        <X className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* Prev */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          aria-label="Previous photo"
          className="absolute left-2 md:left-5 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <ChevronLeft className="h-7 w-7" aria-hidden="true" />
        </button>
      )}

      {/* Next */}
      {items.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          aria-label="Next photo"
          className="absolute right-2 md:right-5 top-1/2 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          <ChevronRight className="h-7 w-7" aria-hidden="true" />
        </button>
      )}

      {/* Stage */}
      <figure
        className="relative mx-4 max-h-[90vh] max-w-[92vw] md:max-w-[80vw]"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={item.src}
          src={item.src}
          srcSet={item.srcSet}
          sizes={item.sizes || "92vw"}
          alt={item.alt}
          width={item.width}
          height={item.height}
          className="block max-h-[80vh] w-auto h-auto mx-auto rounded-lg shadow-2xl"
        />
        <figcaption
          id="lightbox-caption"
          className="mt-3 mx-auto max-w-3xl text-center text-sm text-white/90"
        >
          <span className="font-semibold">{caption}</span>
          <span className="block text-[11px] mt-1 text-white/60 uppercase tracking-[0.18em]">
            {item.category ? `${item.category} · ` : ""}{positionLabel}
          </span>
        </figcaption>
      </figure>

      {/* Live region for screen readers as user navigates */}
      <div className="sr-only" aria-live="polite">{positionLabel}: {item.alt}</div>
    </div>,
    document.body
  );
}
